import type { UnitOperationKind } from "@/lib/api/courseUnits";

const PREFIX = "imam:unit-guidance:v1:";
export const GUIDANCE_RECOVERY_CHANGED = "imam:guidance-recovery-changed";
export const GUIDANCE_RECOVERY_CLEARED = "imam:guidance-recovery-cleared";
export type PendingGuidance = { id: string; unitId: string; kind: UnitOperationKind; snapshot?: string };
export function guidanceKey(tenant: string, actor: string, course: string, unit: string) {
  return `${PREFIX}${tenant}:${actor}:${course}:${unit}`;
}
export function readRecovery<T>(key: string): T | null {
  try { return JSON.parse(sessionStorage.getItem(key) ?? "null") as T | null; }
  catch { return null; }
}
export function writeRecovery(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}
export function readPending(key: string): PendingGuidance | null {
  try {
    const direct = JSON.parse(localStorage.getItem(key) ?? "null") as PendingGuidance | null;
    if (direct) return direct;
    const prefix = key.slice(0, key.lastIndexOf(":") + 1), id = key.slice(prefix.length);
    for (let i = 0; i < localStorage.length; i++) {
      const candidate = localStorage.key(i);
      if (candidate?.startsWith(prefix)) {
        const pending = JSON.parse(localStorage.getItem(candidate) ?? "null") as PendingGuidance | null;
        if (pending?.unitId === id) return pending;
      }
    }
    return null;
  }
  catch { return null; }
}
export function writePending(key: string, value: PendingGuidance | null, expectedId?: string) {
  if (value) localStorage.setItem(key, JSON.stringify(value));
  else {
    const current = readPending(key);
    if (expectedId && current?.id !== expectedId) return;
    const prefix = key.slice(0, key.lastIndexOf(":") + 1);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const candidate = localStorage.key(i);
      if (candidate?.startsWith(prefix) && readPending(candidate)?.id === current?.id) localStorage.removeItem(candidate);
    }
    localStorage.removeItem(key);
  }
  window.dispatchEvent(new Event(GUIDANCE_RECOVERY_CHANGED));
}
export function clearGuidanceRecovery(prefix = PREFIX) {
  window.dispatchEvent(new CustomEvent(GUIDANCE_RECOVERY_CLEARED, { detail: prefix }));
  for (const name of ["sessionStorage", "localStorage"] as const) {
    try {
      const storage = window[name];
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) storage.removeItem(key);
      }
    } catch { /* A denied store cannot prevent sign-out or clearing another store. */ }
  }
  window.dispatchEvent(new Event(GUIDANCE_RECOVERY_CHANGED));
}

/** The plan a "New plan" card in this browser is already working on, if any. */
export function recoveredNewPlanId(tenantId: string, userId: string, courseId: string): string | null {
  const key = guidanceKey(tenantId, userId, courseId, "new");
  return readPending(key)?.unitId ?? readRecovery<{ unitId?: string }>(key)?.unitId ?? null;
}
